import type {
  SocialScoreRun,
  SocialScoreRunResult,
} from '@retracedgmbh/modules/social-score/contracts';
import type {
  AiExtractionFeedbackPayload,
  AuditAccessControlResponse,
  AuditAccessRequestsResponse,
  AuditAiWorkflow,
  AuditDetail,
  AuditDigitizationRecord,
  AuditPatchPayload,
  AuditsParams,
  AuditsResponse,
  AuditTypesResponse,
  AuditVersionHistoryResponse,
  CapImportContext,
  CreateAuditPayload,
  CreateDigitizedSyncHistoryPayload,
  ImportActionsPayload,
} from '@/api/audits/types';
import { API_ROOT_URL, BACKEND_URL } from '@/lib/constants/api';
import { ApiError, apiFetch, readApiErrorCode } from '@/lib/utils/apiFetch';

// Migrated out of the legacy audits-service to the new-structure gateway
// route (`GET /api/audits`, same `{ data, meta }` envelope). The legacy
// `withAllStatus` param is no longer sent — the legacy schema always stripped
// it before the handler ran, so it never did anything.
export async function fetchAudits(
  params: AuditsParams,
): Promise<AuditsResponse> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page - 1));
  qs.set('count', String(params.count));
  if (params.companyId) qs.set('companyId', params.companyId);
  if (params.sort) qs.set('sort', params.sort);
  if (params.filter) qs.set('filter', params.filter);
  if (params.isForNetwork !== undefined)
    qs.set('isForNetwork', String(params.isForNetwork));
  if (params.isCompanyProfile !== undefined)
    qs.set('isCompanyProfile', String(params.isCompanyProfile));
  return apiFetch<AuditsResponse>(
    `/api/audits?${qs.toString()}`,
    {},
    API_ROOT_URL,
  );
}

// Migrated out of the legacy audits-service to the new-structure gateway
// route (`GET /api/audits/:id`, `apiSuccess` envelope). The legacy
// audits-service path (`GET /v1/audits/:id`) no longer exists.
export async function fetchAuditById(
  id: string,
): Promise<{ data: AuditDetail }> {
  return apiFetch<{ data: AuditDetail }>(`/api/audits/${id}`, {}, API_ROOT_URL);
}

// Migrated out of the legacy audits-service to the new-structure gateway
// route (`GET /api/audits/:id/versions`, same `{ data, meta }` envelope).
// The legacy audits-service path (`GET /v1/audits/:id/versions`) no longer
// exists.
/** The audit's newest COMPLETED social score run: total, per-area breakdown
 *  and every graded finding. Null when it has never been scored — not an
 *  error. */
export async function fetchAuditSocialScoreMatches(
  id: string,
): Promise<{ data: SocialScoreRunResult | null }> {
  return apiFetch<{ data: SocialScoreRunResult | null }>(
    `/api/audits/${id}/social-score/runs/latest/matches`,
    {},
    API_ROOT_URL,
  );
}

/** The audit's most recent run, whatever its status — what tells a screen a
 *  run is still in flight. Null when the audit has never been scored (the
 *  route 404s, which is not an error worth surfacing here). */
export async function fetchAuditSocialScoreRun(
  id: string,
): Promise<{ data: SocialScoreRun } | null> {
  try {
    return await apiFetch<{ data: SocialScoreRun }>(
      `/api/audits/${id}/social-score/runs/latest`,
      {},
      API_ROOT_URL,
    );
  } catch (error) {
    // ONLY the "never scored" case. A blanket catch here turned every real
    // failure — a 500, a dropped connection — into "this audit has no score",
    // which is indistinguishable on screen from the truth and impossible to
    // debug from the UI.
    if (readApiErrorCode(error) === 'social_score_run_not_found') return null;
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

/** Queues a social score run. 202 with a run id to poll — the model call
 *  takes tens of seconds, so it does not run on the request thread. */
export async function postAuditSocialScoreRun(
  id: string,
): Promise<{ data: { runId: string; status: string } }> {
  return apiFetch<{ data: { runId: string; status: string } }>(
    `/api/audits/${id}/social-score/runs`,
    { method: 'POST' },
    API_ROOT_URL,
  );
}

/** Un-scores the audit. Idempotent. */
export async function deleteAuditSocialScoreRuns(id: string): Promise<void> {
  await apiFetch<void>(
    `/api/audits/${id}/social-score/runs`,
    { method: 'DELETE' },
    API_ROOT_URL,
  );
}

export async function fetchAuditVersionHistory(
  id: string,
  qs: string,
): Promise<AuditVersionHistoryResponse> {
  return apiFetch<AuditVersionHistoryResponse>(
    `/api/audits/${id}/versions?${qs}`,
    {},
    API_ROOT_URL,
  );
}

// Migrated out of the legacy audits-service to the new-structure gateway
// route (`GET /api/audit-types`, same `{ data, meta }` envelope). The legacy
// audits-service path (`GET /v1/audit-types`) no longer exists.
export async function fetchAuditTypes(): Promise<AuditTypesResponse> {
  const qs = new URLSearchParams({ sort: 'name:ASC', count: '200' });
  return apiFetch<AuditTypesResponse>(
    `/api/audit-types?${qs.toString()}`,
    {},
    API_ROOT_URL,
  );
}

// Migrated out of the legacy audits-service to the new-structure gateway
// route (`PATCH /api/audits/:id`). The legacy audits-service path
// (`PATCH /v1/audits/:id`) no longer exists.
export async function patchAudit(
  id: string,
  body: AuditPatchPayload,
): Promise<void> {
  await apiFetch<void>(
    `/api/audits/${id}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

// Migrated out of the legacy audits-service to the new-structure gateway
// route (`POST /api/audits/:id/request`). The legacy audits-service path
// (`POST /v1/audits/:id/request`) no longer exists.
export async function requestAuditAccess(id: string): Promise<void> {
  await apiFetch<void>(
    `/api/audits/${id}/request`,
    { method: 'POST' },
    API_ROOT_URL,
  );
}

/**
 * Cancels the caller's own open access request.
 *
 * Pairs with `requestAuditAccess` above: both must work on
 * `audits_service_audit_access_requests`, where the request flow records it.
 * The legacy `/v3/audits/:id/cancel-request` this once called deleted an
 * `audit_permissions` row that flow never creates — so it cancelled nothing
 * [DELTA-2545]. Migrated to the new-structure gateway route
 * (`POST /api/audits/:id/cancel-request`), which writes that same table; the
 * legacy audits-service path (`POST /v1/audits/:id/cancel-request`) no longer
 * exists.
 */
export async function cancelAuditRequest(id: string): Promise<void> {
  await apiFetch<void>(
    `/api/audits/${id}/cancel-request`,
    { method: 'POST' },
    API_ROOT_URL,
  );
}

// Migrated out of the legacy audits-service to the new-structure gateway
// route (`PATCH /api/audits/bulk-archive`). The legacy audits-service path
// (`PATCH /v1/audits/bulk-archive`) no longer exists.
export async function bulkArchiveAudits(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/api/audits/bulk-archive',
    { method: 'PATCH', body: JSON.stringify(ids) },
    API_ROOT_URL,
  );
}

// Migrated out of the legacy audits-service to the new-structure gateway
// route (`PATCH /api/audits/bulk-unarchive`). The legacy audits-service path
// (`PATCH /v1/audits/bulk-unarchive`) no longer exists.
export async function bulkUnarchiveAudits(ids: string[]): Promise<void> {
  await apiFetch<void>(
    '/api/audits/bulk-unarchive',
    { method: 'PATCH', body: JSON.stringify(ids) },
    API_ROOT_URL,
  );
}

export async function createAudit(
  body: CreateAuditPayload,
): Promise<{ data: { id: string } }> {
  // Create was migrated out of the legacy audits-service to the new
  // `POST /api/assets`-style gateway route (`POST /api/audits`, `apiSuccess`
  // envelope) — mirrors the legacy create body. The old audits-service path no
  // longer exists.
  return apiFetch<{ data: { id: string } }>(
    '/api/audits',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * Creates an audit as the answer to a questionnaire AUDIT question.
 *
 * Carries **no company id and no `isPublic`**: the server files the audit on the
 * company the answer request is about and shares it with that company and with
 * the brand that asked. `createAudit` takes the company in its body and then
 * shares to it through a connection-checked path a delegated answering party can
 * never satisfy — the share threw and was swallowed, so the factory never got
 * access to its own audit (DELTA-2415). `isPublic` is refused rather than
 * forwarded: a public audit fans a read grant out to the uploader's entire
 * network.
 *
 * Files are passed by id; the route resolves the bucket and copies them into
 * private storage.
 */
export async function createQuestionnaireRequestAudit(
  requestId: string,
  payload: {
    auditTypeId: string;
    auditDate?: string | null;
    nextPlannedAuditDate?: string | null;
    auditedBy?: string | null;
    facilityScore?: string | null;
    referenceNumber?: string | null;
    auditReportFileId?: string | null;
    correctiveActionPlanFileId?: string | null;
  },
): Promise<{ data: { id: string } }> {
  return apiFetch<{ data: { id: string } }>(
    `/api/questionnaires/requests/${requestId}/audits`,
    { method: 'POST', body: JSON.stringify(payload) },
    API_ROOT_URL,
  );
}

export async function triggerAuditAiWorkflow(body: {
  auditFileId?: string;
  capFileId?: string;
  /** CAP-only imports only — stamped onto the workflow metadata so the job stays
   *  resumable from the AI-processing queue. Omitted for audit-file flows. */
  importContext?: CapImportContext;
}): Promise<{ id: string }> {
  const res = await apiFetch<{ data: { id: string } }>(
    '/capa/process',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
  return res.data;
}

export async function getAuditAiWorkflow(
  workflowId: string,
): Promise<AuditAiWorkflow> {
  const res = await apiFetch<{ data: AuditAiWorkflow }>(
    `/capa/workflows/${workflowId}`,
    {},
    BACKEND_URL,
  );
  return res.data;
}

export async function cancelAuditAiWorkflow(workflowId: string): Promise<void> {
  await apiFetch<void>(
    '/capa/process/cancel',
    { method: 'DELETE', body: JSON.stringify({ workflowId }) },
    BACKEND_URL,
  );
}

export async function importCapaActions(
  body: ImportActionsPayload,
): Promise<{ actionIds: string[] }> {
  const res = await apiFetch<{ data: { actionIds: string[] } }>(
    '/capa/import-actions',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
  return res.data;
}

export async function getAuditAccessControl(
  auditId: string,
): Promise<{ data: AuditAccessControlResponse }> {
  // Refactored into modules/companies, now served under the `/api/assets`
  // gateway mount (`GET /assets/:assetId/access-control`, `apiSuccess` envelope).
  return apiFetch<{ data: AuditAccessControlResponse }>(
    `/api/assets/${auditId}/access-control`,
    {},
    API_ROOT_URL,
  );
}

// Migrated out of the legacy audits-service to the new-structure gateway
// route (`GET /api/audits/:id/access-requests`, same `{ data, meta }`
// envelope). The legacy audits-service path
// (`GET /v1/audit-access-requests/:id`) no longer exists.
export async function getAuditAccessRequests(
  auditId: string,
): Promise<AuditAccessRequestsResponse> {
  const filter = encodeURIComponent(JSON.stringify({ status: 'REQUESTED' }));
  return apiFetch<AuditAccessRequestsResponse>(
    `/api/audits/${auditId}/access-requests?filter=${filter}`,
    {},
    API_ROOT_URL,
  );
}

export async function shareAuditWithCompany(params: {
  auditId: string;
  companyId: string;
}): Promise<void> {
  // `POST /assets/:assetId/shares` — one company per call, returns 204.
  await apiFetch<void>(
    `/api/assets/${params.auditId}/shares`,
    {
      method: 'POST',
      body: JSON.stringify({ companyId: params.companyId }),
    },
    API_ROOT_URL,
  );
}

/** `POST /ai-extraction-feedback` — thumbs up/down on an AI extraction review.
 *  The audit-AI dialogs use THIS route (not `/ai-feedback`): it is keyed on the
 *  reviewed audit rather than on a (file, asset, process) tuple, so it also
 *  works for CAP-only imports (which have no audit file) and accepts repeat
 *  feedback on the same job. Mirrors old FE `submitAiExtractionFeedback`. */
export async function submitAiExtractionFeedback(
  body: AiExtractionFeedbackPayload,
): Promise<void> {
  await apiFetch<void>(
    '/ai-extraction-feedback',
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

export async function submitAiFeedback(body: {
  fileId: string;
  assetId: string;
  assetType: string;
  aiProcessType: string;
  vote: 0 | 1;
  /** Free-text feedback. Captured by the UI; only sent when the backend
   *  accepts it (omitted from the wire payload otherwise to avoid 500s). */
  text?: string;
}): Promise<void> {
  // BE currently 500s when an unknown `text` field is included. Strip it
  // from the wire payload until the route is extended; the UI still
  // collects the message so we can flip this back on without UX changes.
  const { text: _text, ...payload } = body;
  await apiFetch<void>(
    '/ai-feedback',
    { method: 'POST', body: JSON.stringify(payload) },
    BACKEND_URL,
  );
}

// ─── AI suggestions sub-flow ────────────────────────────────────────────────

export type CapaWorkflowType =
  | 'CLASSIFICATION'
  | 'FIELD_EXTRACTION'
  | 'CAPA_CLASSIFICATION'
  | 'CAPA_CAP_FILE_DATA_EXTRACTION'
  | 'EXTRACT_FINDINGS'
  | 'CAPS_CREATION';

export interface CapaWorkflow {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  percentage: number;
  metadata?: Record<string, unknown> | null;
  steps?: { type: CapaWorkflowType; status: string }[];
}

/** Append additional processing steps to an existing capa workflow and
 *  re-open it to PROCESSING. Used to layer EXTRACT_FINDINGS + CAPS_CREATION
 *  on top of an already-completed upload workflow (AI suggestions flow). */
export async function appendWorkflowSteps(
  workflowId: string,
  workflows: CapaWorkflowType[],
): Promise<CapaWorkflow> {
  const res = await apiFetch<{ data: CapaWorkflow }>(
    `/capa/workflows/${workflowId}/steps`,
    { method: 'POST', body: JSON.stringify({ workflows }) },
    BACKEND_URL,
  );
  return res.data;
}

/** Poll a capa workflow until terminal. Mirrors the upload-analysis path. */
export async function fetchCapaWorkflow(
  workflowId: string,
): Promise<CapaWorkflow> {
  const res = await apiFetch<{ data: CapaWorkflow }>(
    `/capa/workflows/${workflowId}`,
    {},
    BACKEND_URL,
  );
  return res.data;
}

// ─── Digitized audits ───────────────────────────────────────────────────────

/** GET /audits/digitization/:auditId — the digitization record (progress +
 *  extracted content). Backend routes live on the monolith (`BACKEND_URL`,
 *  no `/v1`). Throws ApiError 404 (`digitization_not_found`) when the audit
 *  has not been digitized yet — the hook maps that to `null`. */
export async function fetchAuditDigitization(
  auditId: string,
): Promise<{ data: AuditDigitizationRecord }> {
  return apiFetch<{ data: AuditDigitizationRecord }>(
    `/audits/digitization/${auditId}`,
    {},
    BACKEND_URL,
  );
}

/** POST /audits/digitization — starts (or restarts) digitization for an audit.
 *  Throws ApiError 409 (`digitization_already_in_progress`) when a run is
 *  already active; the caller treats that as a benign "already analyzing". */
export async function startAuditDigitization(
  auditId: string,
): Promise<{ data: AuditDigitizationRecord }> {
  return apiFetch<{ data: AuditDigitizationRecord }>(
    '/audits/digitization',
    { method: 'POST', body: JSON.stringify({ auditId }) },
    BACKEND_URL,
  );
}

/** POST /audits/digitization/:auditId/history — records a datapoints-sync
 *  history entry after the user pushes verified answers to the profile. */
export async function createDigitizedAuditSyncHistory(
  auditId: string,
  body: CreateDigitizedSyncHistoryPayload,
): Promise<void> {
  await apiFetch<void>(
    `/audits/digitization/${auditId}/history`,
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

// ─── Sync digitized answers → company profile ───────────────────────────────
// Mirror of the v1 sync flow. The template-answer routes live on the main API
// gateway (`/api/companies/templates/...`), NOT the companies-service micro-
// service. `templateId` is an arbitrary profile/impact-area id (not the narrow
// TemplateId union), so these deliberately do not reuse `@/api/companies`.

/** Current company-profile answers for a template, keyed by retracedId. */
export async function fetchDigitizedProfileTemplate(
  templateId: string,
  companyId: string,
): Promise<{ data?: Record<string, unknown> }> {
  return apiFetch<{ data?: Record<string, unknown> }>(
    `/api/companies/templates/${templateId}/profile/${companyId}`,
    {},
    API_ROOT_URL,
  );
}

/** Write verified digitized answers into one template of the company profile. */
export async function patchDigitizedTemplateAnswer(
  templateId: string,
  body: {
    byCompanyId: string;
    forCompanyId: string;
    onBehalfOfCompanyId: string;
    answer: Record<string, unknown>;
  },
): Promise<void> {
  await apiFetch<void>(
    `/api/companies/templates/${templateId}/answer`,
    { method: 'PATCH', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}
